import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Clock, Menu } from "lucide-react";
import { NotificationBell } from "./NotificationBell";
import {
  myDashboard,
  type MyCourseSummary,
  type NextLessonOut,
  type StudentDashboardOut,
} from "../api/assignments";
import { ApiError } from "../api/client";
import { fileUrl } from "../api/files";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { MobileTabBar } from "./MobileTabBar";
import { FieldPracticeSection } from "./FieldPracticeSection";
import { currentMoscowHour } from "../utils/datetime";

function greetingFor(hour: number, firstName: string): string {
  const h = hour;
  let phrase = "Здравствуйте";
  if (h >= 5 && h < 12) phrase = "Доброе утро";
  else if (h >= 12 && h < 17) phrase = "Добрый день";
  else if (h >= 17 && h < 23) phrase = "Добрый вечер";
  else phrase = "Доброй ночи";
  return `${phrase}, ${firstName}!`;
}

function firstName(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  // ФИО обычно "Фамилия Имя Отчество" — берём вторую часть, иначе первую
  if (parts.length >= 2) return parts[1] as string;
  if (parts.length === 1) return parts[0] as string;
  return "";
}

const LESSON_TYPE_LABEL: Record<string, string> = {
  text: "Теория",
  video: "Видео",
  file: "Материал",
  checklist: "Чек-лист",
  quiz: "Тест",
  mixed: "Урок",
};

export function StudentDashboard() {
  const { user } = useCurrentUser();
  const [data, setData] = useState<StudentDashboardOut | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void myDashboard()
      .then(setData)
      .catch((err) => {
        setError(
          err instanceof ApiError ? `Ошибка ${err.status}` : "Сеть недоступна",
        );
        setData({ current_course: null, next_lesson: null });
      });
  }, []);

  if (!user) return null;

  const greeting = greetingFor(currentMoscowHour(), firstName(user.full_name));

  return (
    <div className="min-h-full bg-surface pb-20 md:pb-8">
      {/* Top bar — mobile-first */}
      <header className="sticky top-0 z-10 border-b border-line bg-surface/95 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
          <button
            type="button"
            className="grid h-9 w-9 place-items-center rounded-lg text-ink-soft transition hover:bg-surface-alt md:hidden"
            aria-label="Меню"
          >
            <Menu size={20} strokeWidth={2} />
          </button>
          <div className="flex flex-col items-center leading-tight md:items-start">
            <span className="text-sm font-bold tracking-tight text-ink">
              Петрополь
            </span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-ink-muted">
              Академия
            </span>
          </div>
          <NotificationBell />

        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 pt-6">
        {user.is_sandbox && (
          <div className="mb-4 flex items-start gap-3 rounded-xl bg-brand-ochre/10 px-4 py-3 ring-1 ring-brand-ochre/30">
            <span aria-hidden className="text-lg leading-none">🧪</span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-brand-ochre">
                Режим песочницы
              </div>
              <p className="mt-0.5 text-xs text-ink-soft">
                Ваш аккаунт — тренировочный. Заявки, экскурсии и спичи не учитываются в боевой системе.
              </p>
            </div>
          </div>
        )}
        <section className="mb-6">
          <h1 className="text-[26px] font-bold leading-tight text-ink">
            {greeting}
          </h1>
          <p className="mt-1 text-sm text-ink-muted">Продолжим обучение?</p>
        </section>

        {error && (
          <div className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        {data === null ? (
          <SkeletonHero />
        ) : data.current_course ? (
          <CurrentCourseCard course={data.current_course} />
        ) : (
          <NoCoursesCard />
        )}

        {data?.next_lesson && data.current_course && (
          <NextActionCard
            lesson={data.next_lesson}
            courseId={data.current_course.course_id}
          />
        )}

        <FieldPracticeSection />
      </main>

      <MobileTabBar active="home" />
    </div>
  );
}

function SkeletonHero() {
  return (
    <div className="mb-6">
      <div className="mb-2 h-3 w-28 animate-pulse rounded bg-surface-alt" />
      <div className="h-36 animate-pulse rounded-2xl bg-surface-alt" />
    </div>
  );
}

function CurrentCourseCard({ course }: { course: MyCourseSummary }) {
  return (
    <section className="mb-6">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">
        Текущий курс
      </div>
      <Link
        to={`/learn/courses/${course.course_id}`}
        className="group relative block overflow-hidden rounded-2xl bg-brand text-white shadow-sm transition hover:bg-brand-dark"
      >
        {course.cover_file_id && (
          <>
            <img
              src={fileUrl(course.cover_file_id)}
              alt=""
              className="absolute inset-0 h-full w-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-brand/95 via-brand/85 to-brand-dark/95" />
          </>
        )}
        <div className="relative p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xl font-bold leading-snug">
                {course.course_title}
              </div>
              <div className="mt-0.5 text-xs uppercase tracking-wide text-white/70">
                {course.is_mandatory ? "Обязательный курс" : "Курс"}
              </div>
            </div>
            <div className="shrink-0 rounded-full bg-white/15 px-2.5 py-1 text-sm font-semibold tabular-nums">
              {course.percent}%
            </div>
          </div>
          <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-white/20">
            <div
              className="h-full rounded-full bg-white transition-all"
              style={{ width: `${course.percent}%` }}
            />
          </div>
          <div className="mt-2 flex items-center justify-between text-xs text-white/80">
            <span>
              {course.lessons_completed} из {course.lessons_total} уроков
              пройдено
            </span>
            {course.overdue && (
              <span className="rounded-full bg-brand-danger/90 px-2 py-0.5 text-[10px] font-semibold uppercase">
                Просрочен
              </span>
            )}
          </div>
        </div>
      </Link>
    </section>
  );
}

function NextActionCard({
  lesson,
  courseId,
}: {
  lesson: NextLessonOut;
  courseId: number;
}) {
  const typeLabel = LESSON_TYPE_LABEL[lesson.lesson_type] ?? "Урок";
  const lessonNumber = `${lesson.module_position}.${lesson.lesson_position}`;
  return (
    <section className="mb-6">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">
        Ближайшее действие
      </div>
      <div className="card p-5">
        <div className="text-xs font-medium text-ink-muted">
          Урок {lessonNumber}
        </div>
        <div className="mt-1 text-base font-semibold leading-snug text-ink">
          «{lesson.lesson_title}»
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="badge badge-brand">{typeLabel}</span>
          {lesson.estimated_minutes && (
            <span className="inline-flex items-center gap-1 text-xs text-ink-muted">
              <Clock size={12} strokeWidth={2} />
              {lesson.estimated_minutes} мин
            </span>
          )}
        </div>
        <Link
          to={`/learn/lessons/${lesson.lesson_id}?module=${lesson.module_id}`}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-brand px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-dark"
          state={{ courseId }}
        >
          Продолжить урок
          <ChevronRight size={16} strokeWidth={2.5} />
        </Link>
      </div>
    </section>
  );
}

function NoCoursesCard() {
  return (
    <section className="mb-6">
      <div className="card p-6 text-center">
        <div className="text-base font-semibold text-ink">
          У вас пока нет курсов
        </div>
        <p className="mt-1 text-sm text-ink-muted">
          Куратор назначит вам курсы в ближайшее время.
        </p>
      </div>
    </section>
  );
}
